import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CILogo } from './CILogo';
import { ComingSoonBadge } from './ComingSoonBadge';
import { IntegrationStatusBadge } from './IntegrationStatusWidget';
import BusinessSwitcher from './BusinessSwitcher';
import { ChevronDown, User, LogOut, X, HelpCircle } from 'lucide-react';
import { toast } from 'sonner';
import CIScoreExplainer from './CIScoreExplainer';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Professional Merchant Header
 * 
 * Clean, minimal, scalable navigation for SaaS product
 * Proper hierarchy, consistent spacing, reduced cognitive load
 * 
 * Updated: Fixed navigation behaviors per user requirements
 */
export const MerchantHeader = ({ user, onLogout, onCreateDeal, isSuperadmin: isSuperadminProp }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [showComingSoonModal, setShowComingSoonModal] = useState(false);
  const [comingSoonFeature, setComingSoonFeature] = useState('');
  const [showCIScoreExplainer, setShowCIScoreExplainer] = useState(false);
  const [isSuperadmin, setIsSuperadmin] = useState(isSuperadminProp || false);

  // Check superadmin status if not provided as prop
  useEffect(() => {
    if (isSuperadminProp === undefined) {
      checkSuperadminStatus();
    } else {
      setIsSuperadmin(isSuperadminProp);
    }
  }, [isSuperadminProp]);

  const checkSuperadminStatus = async () => {
    try {
      const token = sessionStorage.getItem('session_token');
      if (!token) return;
      
      const response = await fetch(`${API_URL}/api/dashboard/superadmin/check`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      
      if (response.ok) {
        const data = await response.json();
        setIsSuperadmin(data.is_superadmin || false);
      } else {
        const errorText = await response.text();
        console.error('[MerchantHeader] Superadmin check failed:', response.status, errorText);
      }
    } catch (error) {
      console.error('[MerchantHeader] Failed to check superadmin status:', error);
    }
  };

  // Navigation structure with proper paths and behaviors
  // Note: Merchant Management is added as a top-level item for superadmins
  const navigation = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      path: '/dashboard',
      description: 'Overview & metrics',
    },
    {
      id: 'deals',
      label: 'Deals',
      path: '/dashboard/deals',
      description: 'Manage your deals',
      submenu: [
        { label: 'All Deals', path: '/dashboard/deals', icon: '🏷️', action: 'navigate' },
        { label: 'Demand & Offers', path: '/dashboard/deals/demand', icon: '📊', badge: 'beta', action: 'navigate' },
        { label: 'Create Deal', path: null, icon: '➕', action: 'create_deal' },
        { label: 'Performance', path: '/dashboard/deals/performance', icon: '📊', action: 'navigate' },
        { label: 'Distribution', path: '/dashboard/deals/distribution', icon: '📡', action: 'navigate' },
      ],
    },
    {
      id: 'analytics',
      label: 'Analytics',
      path: '/dashboard/analytics',
      description: 'Performance insights',
      submenu: [
        { label: 'Performance Analytics', path: '/dashboard/analytics', icon: '📈', action: 'navigate' },
        { label: 'Conversion Insights', path: '/dashboard/analytics/conversion', icon: '🎯', action: 'navigate' },
        { label: 'Category Intelligence', path: null, icon: '🗂️', badge: 'soon', action: 'coming_soon' },
        { label: 'Consumer Behavior', path: null, icon: '👥', badge: 'soon', action: 'coming_soon' },
      ],
    },
    {
      id: 'revenue',
      label: 'Revenue',
      path: '/dashboard/affiliate',
      description: 'Monetization suite',
      submenu: [
        { label: 'Affiliate Network', path: '/dashboard/affiliate', icon: '💰', action: 'navigate' },
        { label: 'Financial Health', path: '/dashboard/financial-health', icon: '🛡️', badge: 'new', action: 'navigate' },
        { label: 'Business Budgeting', path: '/dashboard/funding/budget', icon: '📊', badge: 'beta', action: 'navigate' },
        { label: 'Creator Network', path: null, icon: '👥', badge: 'soon', action: 'coming_soon' },
        { label: 'Sponsorships', path: null, icon: '🤝', badge: 'soon', action: 'coming_soon' },
        { label: 'Ads Marketplace', path: null, icon: '📢', badge: 'soon', action: 'coming_soon' },
      ],
    },
    {
      id: 'distribution',
      label: 'Distribution',
      path: '/dashboard/twitter',
      description: 'Social & community',
      submenu: [
        { label: 'Twitter / X Agent', path: '/dashboard/twitter', icon: '🐦', badge: 'new', action: 'navigate' },
        { label: 'Deal Distribution', path: '/dashboard/deals/distribution', icon: '📡', action: 'navigate' },
      ],
    },
    {
      id: 'intelligence',
      label: 'Intelligence',
      path: '/dashboard/margin-guard',
      description: 'AI & predictive features',
      isPremium: true,
      submenu: [
        { label: 'Margin Guard', path: '/dashboard/margin-guard', icon: '🛡️', action: 'navigate' },
        { label: 'Promise Health', path: '/dashboard/promise-health', icon: '📊', action: 'navigate' },
        { label: 'Exception Radar', path: '/dashboard/exception-radar', icon: '⚡', action: 'navigate' },
        { label: 'AI Deal Recommendations', path: '/dashboard/recommendations', icon: '✨', badge: 'beta', action: 'navigate' },
        { label: 'Customer Feedback & NPS', path: '/dashboard/feedback', icon: '💬', badge: 'new', action: 'navigate' },
        { label: 'Product Discovery & Launch', path: '/dashboard/products/engine', icon: '🚀', badge: 'new', action: 'navigate' },
        { label: 'Pricing Intelligence', path: '/dashboard/pricing-intelligence', icon: '💰', badge: 'pro-plus', action: 'navigate' },
        { label: 'Experiments Lab', path: '/dashboard/experiments', icon: '🧪', badge: 'labs', action: 'navigate' },
      ],
    },
    {
      id: 'settings',
      label: 'Settings',
      path: '/dashboard/settings',
      description: 'Configure your account',
      submenu: [
        { label: 'Integrations', path: '/dashboard/settings', icon: '🔌', action: 'navigate' },
        { label: 'Slack Integration', path: '/dashboard/settings/integrations/slack', icon: '💬', action: 'navigate' },
        { label: 'Action Queue', path: '/dashboard/approvals', icon: '⏳', action: 'navigate' },
        { label: 'Billing & Plans', path: '/dashboard/settings', icon: '💳', action: 'navigate' },
        { label: 'Team & Alerts', path: '/dashboard/team', icon: '👥', action: 'navigate' },
        ...(isSuperadmin ? [{ label: 'Merchant Management', path: '/dashboard/admin/merchants', icon: '🛡️', action: 'navigate', badge: 'admin' }] : []),
      ],
    },
    {
      id: 'developers',
      label: 'Developers',
      path: '/dashboard/developers',
      description: 'API & Integrations',
      showStatusBadge: true,
      submenu: [
        { label: 'API Keys', path: '/dashboard/developers', icon: '🔑', action: 'navigate' },
        { label: 'Web Integration', path: '/dashboard/developers', icon: '🌐', action: 'navigate' },
        { label: 'Signals API', path: '/docs/signals', icon: '📊', action: 'navigate' },
        { label: 'Documentation', path: '/docs', icon: '📚', action: 'navigate' },
      ],
    },
    // Add Admin as a top-level nav item for superadmins (more visible)
    ...(isSuperadmin ? [{
      id: 'admin',
      label: '🛡️ Admin',
      path: '/dashboard/admin/merchants',
      description: 'Superadmin controls',
      isAdmin: true,
    }] : []),
  ];

  const isActive = (path) => {
    if (!path) return false;
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const handleNavClick = (item, e) => {
    if (item.submenu) {
      e.preventDefault();
      setActiveDropdown(activeDropdown === item.id ? null : item.id);
    } else {
      navigate(item.path);
      setActiveDropdown(null);
    }
  };

  const handleSubmenuClick = (subitem) => {
    setActiveDropdown(null);
    
    switch (subitem.action) {
      case 'navigate':
        if (subitem.path) {
          navigate(subitem.path);
        }
        break;
      
      case 'create_deal':
        // Trigger the create deal modal in the parent component
        if (onCreateDeal) {
          onCreateDeal();
        } else {
          // Fallback: navigate to deals page with create param
          navigate('/dashboard/deals?create=true');
        }
        break;
      
      case 'coming_soon':
        setComingSoonFeature(subitem.label);
        setShowComingSoonModal(true);
        break;
      
      default:
        if (subitem.path) {
          navigate(subitem.path);
        }
    }
  };

  return (
    <>
      <header className="sticky top-0 z-40 bg-[#0A1628]/95 backdrop-blur-sm border-b border-[#1E3A5F]">
        <div className="max-w-[1600px] mx-auto">
          <div className="flex items-center justify-between h-16 px-6">
            {/* Logo + Business Switcher */}
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/dashboard')}
                className="flex items-center hover:opacity-80 transition-opacity"
                data-testid="ci-logo-button"
              >
                <CILogo size={40} showText={true} theme="dark" />
              </button>
              <div className="w-px h-6 bg-[#1E3A5F]" />
              <BusinessSwitcher />
            </div>

            {/* Navigation */}
            <nav className="flex items-center gap-1">
              {navigation.map((item) => (
                <div key={item.id} className="relative">
                  <button
                    onClick={(e) => handleNavClick(item, e)}
                    className={`
                      px-4 py-2 rounded-md text-[15px] font-medium transition-all
                      flex items-center gap-1.5
                      ${isActive(item.path)
                        ? 'text-white bg-[#1E3A5F]'
                        : 'text-[#9CB0C7] hover:text-white hover:bg-[#1E3A5F]/50'
                      }
                      ${item.isPremium ? 'relative' : ''}
                    `}
                    data-testid={`nav-${item.id}`}
                  >
                    {item.label}
                    {item.showStatusBadge && <IntegrationStatusBadge />}
                    {item.submenu && !item.showStatusBadge && (
                      <ChevronDown
                        className={`w-4 h-4 transition-transform ${activeDropdown === item.id ? 'rotate-180' : ''}`}
                      />
                    )}
                    {item.isPremium && (
                      <span className="absolute -top-1 -right-1 w-2 h-2 bg-purple-500 rounded-full animate-pulse"></span>
                    )}
                  </button>

                  {/* Dropdown Menu */}
                  {item.submenu && activeDropdown === item.id && (
                    <div className="absolute top-full left-0 mt-1 w-64 bg-[#0E1426] border border-[#1E3A5F] rounded-lg shadow-2xl overflow-hidden z-50">
                      <div className="p-2">
                        {item.submenu.map((subitem, index) => (
                          <button
                            key={index}
                            onClick={() => handleSubmenuClick(subitem)}
                            className="w-full flex items-center justify-between px-3 py-2.5 rounded-md text-sm text-[#9CB0C7] hover:text-white hover:bg-[#1E3A5F] transition-colors text-left group"
                            data-testid={`submenu-${subitem.label.toLowerCase().replace(/ /g, '-')}`}
                          >
                            <div className="flex items-center gap-2.5">
                              <span className="text-base">{subitem.icon}</span>
                              <span className="font-medium">{subitem.label}</span>
                            </div>
                            {subitem.badge && (
                              <div className="flex-shrink-0">
                                {subitem.badge === 'pro-plus' ? (
                                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-purple-500/20 border border-purple-500/40 text-purple-300 font-semibold">
                                    PRO+
                                  </span>
                                ) : subitem.badge === 'admin' ? (
                                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-500/20 border border-red-500/40 text-red-300 font-semibold">
                                    ADMIN
                                  </span>
                                ) : subitem.badge === 'beta' ? (
                                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500/20 border border-amber-500/40 text-amber-300 font-semibold">
                                    BETA
                                  </span>
                                ) : (
                                  <ComingSoonBadge size="xs" variant={subitem.badge} showIcon={false} />
                                )}
                              </div>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </nav>

            {/* Help & Account Section */}
            <div className="flex items-center gap-2">
              {/* CI Score Help Button */}
              <button
                onClick={() => setShowCIScoreExplainer(true)}
                className="flex items-center gap-1.5 px-3 py-2 rounded-md text-[#9CB0C7] hover:text-white hover:bg-[#1E3A5F] transition-all text-sm"
                data-testid="ci-score-help-button"
              >
                <HelpCircle className="w-4 h-4" />
                <span className="hidden sm:inline">What is CI Score?</span>
              </button>

              {/* Account Menu */}
              <div className="relative">
              <button
                onClick={() => setActiveDropdown(activeDropdown === 'account' ? null : 'account')}
                className="flex items-center gap-2 px-3 py-2 rounded-md text-[#9CB0C7] hover:text-white hover:bg-[#1E3A5F] transition-all"
                data-testid="account-menu-button"
              >
                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#3EF1C7] to-[#7A5CFF] flex items-center justify-center">
                  <span className="text-xs font-bold text-[#0A1628]">
                    {user?.name?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase() || 'M'}
                  </span>
                </div>
                <ChevronDown className={`w-4 h-4 transition-transform ${activeDropdown === 'account' ? 'rotate-180' : ''}`} />
              </button>

              {/* Account Dropdown */}
              {activeDropdown === 'account' && (
                <div className="absolute top-full right-0 mt-1 w-56 bg-[#0E1426] border border-[#1E3A5F] rounded-lg shadow-2xl overflow-hidden z-50">
                  <div className="p-3 border-b border-[#1E3A5F]">
                    <p className="text-sm font-medium text-white truncate">{user?.name || 'Merchant'}</p>
                    <p className="text-xs text-[#9CB0C7] truncate">{user?.email}</p>
                  </div>
                  <div className="p-2">
                    <button
                      onClick={() => {
                        navigate('/dashboard/settings');
                        setActiveDropdown(null);
                      }}
                      className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-sm text-[#9CB0C7] hover:text-white hover:bg-[#1E3A5F] transition-colors text-left"
                    >
                      <User className="w-4 h-4" />
                      <span>Profile</span>
                    </button>
                    <button
                      onClick={() => {
                        onLogout();
                        setActiveDropdown(null);
                      }}
                      className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-sm text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-colors text-left"
                      data-testid="logout-button"
                    >
                      <LogOut className="w-4 h-4" />
                      <span>Sign Out</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
            </div>
          </div>
        </div>

        {/* Close dropdowns on outside click */}
        {activeDropdown && (
          <div
            className="fixed inset-0 z-30"
            onClick={() => setActiveDropdown(null)}
          />
        )}
      </header>

      {/* CI Score Explainer Modal */}
      {showCIScoreExplainer && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-[#0E1426] rounded-lg border border-[#22304A] max-w-3xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
            <div className="flex items-center justify-between p-6 border-b border-[#22304A]">
              <div>
                <h2 className="text-2xl font-bold text-white">What is CI Score?</h2>
                <p className="text-[#9CB0C7] text-sm mt-1">Commerce Index Intelligence Scoring System</p>
              </div>
              <button
                onClick={() => setShowCIScoreExplainer(false)}
                className="p-2 rounded-lg hover:bg-[#1E3A5F] transition-colors text-[#9CB0C7] hover:text-white"
                data-testid="close-ci-score-modal"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="overflow-y-auto max-h-[calc(90vh-100px)]">
              <CIScoreExplainer showButton={false} />
            </div>
          </div>
        </div>
      )}

      {/* Coming Soon Modal */}
      {showComingSoonModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-[#0E1426] rounded-lg border border-[#22304A] max-w-sm w-full p-6 text-center">
            <div className="w-16 h-16 rounded-full bg-[#7A5CFF]/10 flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">🚀</span>
            </div>
            <h3 className="text-xl font-bold text-white mb-2">Coming Soon!</h3>
            <p className="text-[#9CB0C7] mb-6">
              <span className="text-white font-medium">{comingSoonFeature}</span> is currently under development. 
              We're working hard to bring you this feature soon!
            </p>
            <button
              onClick={() => setShowComingSoonModal(false)}
              className="w-full px-6 py-3 rounded-lg bg-[#7A5CFF] text-white font-semibold hover:bg-[#7A5CFF]/90 transition-colors"
              data-testid="coming-soon-ok-button"
            >
              Got it!
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default MerchantHeader;
